import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  useCompaniesStore,
  type Company,
} from "../../../store/admin/companiesStore";

const MENU_WIDTH = 288;
const MENU_GAP = 8;

interface CompanyMenuProps {
  value: string | null | undefined;
  onChange: (companyId: string | null) => void;
  isClearable?: boolean;
  companies?: Company[];
  className?: string;
}

const companyLabel = (company: Company): string =>
  company.name || company.business_name || company.id;

export default function CompanyMenu({
  value,
  onChange,
  isClearable = false,
  companies: companiesProp,
  className = "",
}: CompanyMenuProps) {
  const { t } = useTranslation("common");

  const storeCompanies = useCompaniesStore((state) => state.allCompanies);
  const isLoading = useCompaniesStore((state) => state.allCompaniesLoading);
  const companies = companiesProp ?? storeCompanies;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxLeft = window.innerWidth - MENU_WIDTH - MENU_GAP;
    setMenuPosition({
      top: rect.bottom + MENU_GAP,
      left: Math.max(MENU_GAP, Math.min(rect.left, maxLeft)),
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      event.key === "Escape" && setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const selected = companies.find((company) => company.id === value) ?? null;
  const normalizedQuery = query.trim().toLowerCase();
  const visibleCompanies = normalizedQuery
    ? companies.filter((company) =>
        `${companyLabel(company)} ${company.rfc ?? ""}`
          .toLowerCase()
          .includes(normalizedQuery),
      )
    : companies;

  const select = (companyId: string | null) => {
    onChange(companyId);
    setIsOpen(false);
  };

  const rowClasses = (isSelected: boolean) =>
    isSelected
      ? "bg-primary-500/10 border-l-2 border-primary-500"
      : "border-l-2 border-transparent hover:bg-subtle";

  const nameClasses = (isSelected: boolean) =>
    isSelected ? "text-primary-600" : "text-content";

  const menu = (
    <>
      <div
        className="fixed inset-0 z-[99998]"
        onClick={() => setIsOpen(false)}
      />
      <div
        role="listbox"
        style={{
          top: menuPosition.top,
          left: menuPosition.left,
          width: MENU_WIDTH,
        }}
        className="fixed z-[99999] bg-card border border-line rounded-2xl overflow-hidden shadow-dropdown"
      >
        <div className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-content-muted bg-surface">
          {t("header.selectedCompany")}
        </div>

        {companies.length > 1 && (
          <div className="p-2 border-b border-line-soft">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("header.searchCompany")}
              autoFocus
              className="w-full px-3 py-1.5 text-sm rounded-lg bg-surface border border-line text-content placeholder-content-faint focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>
        )}

        <div className="max-h-72 overflow-y-auto">
          {isClearable && !normalizedQuery && (
            <button
              type="button"
              onClick={() => select(null)}
              className={`w-full text-left px-3 py-2.5 transition-colors ${rowClasses(!selected)}`}
            >
              <div
                className={`text-sm font-medium truncate ${nameClasses(!selected)}`}
              >
                {t("header.allCompanies")}
              </div>
            </button>
          )}

          {visibleCompanies.map((company) => {
            const isSelected = company.id === value;
            return (
              <button
                key={company.id}
                type="button"
                onClick={() => select(company.id)}
                className={`w-full text-left px-3 py-2.5 transition-colors ${rowClasses(isSelected)}`}
              >
                <div
                  className={`text-sm font-medium truncate ${nameClasses(isSelected)}`}
                >
                  {companyLabel(company)}
                </div>
                {company.rfc && (
                  <div className="text-xs text-content-muted truncate">
                    RFC: {company.rfc}
                  </div>
                )}
              </button>
            );
          })}

          {visibleCompanies.length === 0 && (
            <div className="px-3 py-3 text-sm text-content-muted">
              {isLoading ? t("general.loading") : t("header.noCompanies")}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium text-content hover:bg-subtle transition-colors whitespace-nowrap"
      >
        <span
          className={`max-w-[200px] truncate ${selected ? "" : "text-content-muted"}`}
        >
          {selected ? companyLabel(selected) : t("header.selectCompany")}
        </span>
        <i
          className={`ri-arrow-down-s-line text-content-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && createPortal(menu, document.body)}
    </div>
  );
}
